import { create } from './store';

export interface ChatState {
  // Current thread information
  currentThreadId: string | null;

  // Attachments state
  attachments: any[];
  newAttachmentsAdded: boolean;

  // Files from SDK
  files: any[];

  // Constraints metadata waiting to be sent
  pendingConstraintsMetadata: { metadata: Record<string, any>; recapText: string | null } | null;

  // Thread metadata received from SDK
  threadMetadata: Record<string, any> | null;

  // Which URL thread id the current threadMetadata snapshot belongs to.
  // Mirrored by useConstraintsMetadata each time it copies sdkThreadMetadata
  // into threadMetadata, alongside the URL's params.threadId at that moment.
  // Used by useThreadConstraintsState to tell "we have metadata for THIS
  // thread" apart from "we still hold the prior thread's metadata while the
  // new SDK provider is mid-connect".
  metadataThreadId: string | null;

  // The most recent thread id for which useConstraintsChangeDetection has
  // run its reconciliation. Lives in the global store (not a hook ref) so
  // it SURVIVES the provider remount that happens on every thread swap.
  // Without this, the change-detection hook can't tell "I just remounted on
  // a different thread" apart from "this is thread creation" — the prior
  // ref-based check always saw `prev === undefined` after remount and
  // skipped cleanup, letting the previous thread's working document diff
  // against the new thread's metadata (and surface every differing field
  // as an agent change → red dots flooding the panel).
  lastReconciledMetadataThreadId: string | null;

  // Conversation flow loading state
  conversationFlowLoading: boolean;

  // Pending initial message to auto-send on chat initialization
  pendingInitialMessage: { text: string; metadata?: Record<string, any> } | null;

  // Portfolio actions state keyed by threadId (shared across all portfolio components)
  portfolioActionsState: Record<string, any>;
}

export interface ChatActions {
  // Thread management
  setCurrentThreadId: (threadId: string | null) => void;

  // Attachments management
  setAttachments: (attachments: any[]) => void;
  addAttachment: (attachment: any) => void;
  removeAttachment: (attachmentId: string) => void;
  setNewAttachmentsAdded: (added: boolean) => void;

  // Files management
  setFiles: (files: any[]) => void;

  // Constraints handling
  setPendingConstraintsMetadata: (
    payload: { metadata: Record<string, any>; recapText: string | null } | null,
  ) => void;

  // Thread metadata handling
  setThreadMetadata: (metadata: Record<string, any> | null) => void;

  // Records which URL thread id the threadMetadata snapshot belongs to.
  // Called from useConstraintsMetadata alongside setThreadMetadata.
  setMetadataThreadId: (threadId: string | null) => void;

  // Called by useConstraintsChangeDetection after each reconciliation pass
  // so a subsequent invocation (or a remount on a different thread) can
  // detect "this is the first time I'm reconciling this thread" by
  // comparing against metadataThreadId.
  setLastReconciledMetadataThreadId: (threadId: string | null) => void;

  // Conversation flow loading state
  setConversationFlowLoading: (loading: boolean) => void;

  // Pending initial message handling
  setPendingInitialMessage: (message: { text: string; metadata?: Record<string, any> } | null) => void;

  // Portfolio actions state handling
  setPortfolioActionsState: (threadId: string, state: any) => void;

  // Reset function
  resetChatState: () => void;
}

const initialState: ChatState = {
  currentThreadId: null,
  attachments: [],
  newAttachmentsAdded: false,
  files: [],
  pendingConstraintsMetadata: null,
  threadMetadata: null,
  metadataThreadId: null,
  lastReconciledMetadataThreadId: null,
  conversationFlowLoading: false,
  pendingInitialMessage: null,
  portfolioActionsState: {},
};

export const chatStore = create<ChatState & ChatActions>()((set, get) => ({
  ...initialState,

  // Thread management
  setCurrentThreadId: (threadId) => set({ currentThreadId: threadId }),

  // Attachments management
  setAttachments: (attachments) => set({ attachments }),
  addAttachment: (attachment) =>
    set((state) => ({
      attachments: [...state.attachments, attachment],
      newAttachmentsAdded: true,
    })),
  removeAttachment: (attachmentId) =>
    set((state) => ({
      attachments: state.attachments.filter((att) => att.id !== attachmentId),
    })),
  setNewAttachmentsAdded: (added) => set({ newAttachmentsAdded: added }),

  // Files management
  setFiles: (files) => set({ files }),

  // Constraints handling
  setPendingConstraintsMetadata: (payload) => set({ pendingConstraintsMetadata: payload }),

  // Thread metadata handling
  setThreadMetadata: (metadata) => set({ threadMetadata: metadata }),
  setMetadataThreadId: (threadId) => set({ metadataThreadId: threadId }),
  setLastReconciledMetadataThreadId: (threadId) =>
    set({ lastReconciledMetadataThreadId: threadId }),

  // Conversation flow loading state
  setConversationFlowLoading: (loading) => set({ conversationFlowLoading: loading }),

  // Pending initial message handling
  setPendingInitialMessage: (message) => set({ pendingInitialMessage: message }),

  // Portfolio actions state handling
  setPortfolioActionsState: (threadId, state) =>
    set({ portfolioActionsState: { ...get().portfolioActionsState, [threadId]: state } }),

  // Reset function
  resetChatState: () => set(initialState),
}));

export default chatStore;
